// supabaseAuthHelpers.js - Auth helpers for Supabase RLS
// Location: src/lib/supabaseAuthHelpers.js

import { supabase } from '../supabaseClient';

/**
 * Require an active Supabase session or throw.
 * Returns { session, user } where user is the auth user object.
 */
export async function requireSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (error) {
    console.error('[requireSession] Error getting session:', error);
    throw new Error('Authentication error: ' + error.message);
  }
  
  if (!session) {
    throw new Error('Not authenticated. Please sign in.');
  }
  
  return { session, user: session.user };
}

/**
 * Get the current authenticated user's UUID (auth.uid()).
 * Returns null if not authenticated.
 */
export async function getAuthUserId() {
  try {
    const { user } = await requireSession();
    return user.id; // UUID string
  } catch {
    return null;
  }
}

/**
 * Look up the auth_user_id (UUID) for an employee by their employee ID (BIGINT).
 * This is needed for RLS policies that check auth_user_id.
 * 
 * The users table should have an `auth_user_id` column linking employee IDs to auth UUIDs.
 * If not found, falls back to the current session user's auth ID.
 */
export async function getAuthUserIdForEmployee(employeeId) {
  if (!employeeId && employeeId !== 0) {
    throw new Error('Employee ID is required');
  }

  // First ensure we have a valid session
  const { user: currentUser } = await requireSession();
  
  // Try to look up the auth_user_id from the users table
  try {
    const { data, error } = await supabase
      .from('users')
      .select('auth_user_id')
      .eq('id', employeeId)
      .maybeSingle();
    
    if (error) {
      console.warn('[getAuthUserIdForEmployee] Lookup error:', error.message);
      // Fall back to current user's auth ID
      return currentUser.id;
    }
    
    if (data?.auth_user_id) {
      return data.auth_user_id;
    }
    
    // No auth_user_id found for this employee - fall back to current user
    console.warn('[getAuthUserIdForEmployee] No auth_user_id for employee', employeeId, '- using current user');
    return currentUser.id;
  } catch (err) {
    console.warn('[getAuthUserIdForEmployee] Error:', err.message);
    return currentUser.id;
  }
}

/**
 * Batch lookup auth_user_ids for multiple employee IDs.
 * Returns a Map<employeeId, authUserId>
 */
export async function getAuthUserIdsForEmployees(employeeIds) {
  if (!employeeIds || employeeIds.length === 0) {
    return new Map();
  }

  const { user: currentUser } = await requireSession();
  const resultMap = new Map();

  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, auth_user_id')
      .in('id', employeeIds);
    
    if (error) {
      console.warn('[getAuthUserIdsForEmployees] Lookup error:', error.message);
      // Fall back: assign current user's auth ID to all
      employeeIds.forEach(id => resultMap.set(id, currentUser.id));
      return resultMap;
    }
    
    // Build map from results
    (data || []).forEach(row => {
      if (row.auth_user_id) {
        resultMap.set(row.id, row.auth_user_id);
      } else {
        // No auth_user_id - use current user as fallback
        resultMap.set(row.id, currentUser.id);
      }
    });
    
    // For any employee IDs not found in DB, use current user
    employeeIds.forEach(id => {
      if (!resultMap.has(id)) {
        resultMap.set(id, currentUser.id);
      }
    });
    
    return resultMap;
  } catch (err) {
    console.warn('[getAuthUserIdsForEmployees] Error:', err.message);
    // Fall back: assign current user's auth ID to all
    employeeIds.forEach(id => resultMap.set(id, currentUser.id));
    return resultMap;
  }
}

/**
 * Get the current session's user email.
 */
export async function getAuthUserEmail() {
  try {
    const { user } = await requireSession();
    return user.email;
  } catch {
    return null;
  }
}

export default {
  requireSession,
  getAuthUserId,
  getAuthUserIdForEmployee,
  getAuthUserIdsForEmployees,
  getAuthUserEmail
};
